import {
    Button,
    Col,
    Divider,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Switch,
    Tooltip,
    Typography,
    Upload,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../configureStore";
import { SignatureDocument, SignatureField } from "../../../utils/configuration";
import { HeartOutlined, InfoCircleOutlined, UploadOutlined } from "@ant-design/icons";
import { UploadChangeParam, UploadFile } from "antd/es/upload";
import { getFileJSON } from "../../../utils";
import { ERROR_MESSAGE } from "../../../redux/reducer/status";
import { SigSignFields } from "../../../interface/signature";
import { useCallback } from "react";
import { getDefaultTrueMarkDocument } from "../../../redux/reducer/institution";
import { useMsal } from "@azure/msal-react";
import { AxiosResponse } from "axios";

interface SignatureDocumentModalParams {
    id: string | null;
    document: SignatureDocument;
    enabled: boolean;
    onClose: () => void;
    updateDocument: (document: SignatureDocument, index: number) => void;
}

export const SignatureDocumentModalForm = ({
    onFinish,
    onCancel,
    document,
}: {
    onFinish: (values: SignatureDocument) => void;
    onCancel?: () => void;
    document?: SignatureDocument;
    enabled: boolean;
}) => {
    const { accounts } = useMsal();
    const dispatch = useDispatch<AppDispatch>();
    const loading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['institution/PATCH_INSTITUTION_STEPS']);
    const [form] = Form.useForm<SignatureDocument>();

    const onChange = useCallback((info: UploadChangeParam<UploadFile<any>>) => {
        if (info.file.originFileObj) {
            getFileJSON(info.file.originFileObj).then((json: SigSignFields[]) => {
                const mapped = json.map<SignatureField>((item) => ({
                    fieldId: item.id,
                    fieldName: item.label,
                }));
                form.setFieldValue('fields', mapped);
            }).catch((err) => {
                dispatch({ type: ERROR_MESSAGE, message: (err as Error).message })
            })
        } else dispatch({ type: ERROR_MESSAGE, message: 'Please upload a file!' })
    }, [form])

    const getDefault = useCallback(() => {
        dispatch(getDefaultTrueMarkDocument({
            onSuccess: ({ data }: AxiosResponse<{ document: SignatureDocument }>) => {
                if (data.document) {
                    form.setFieldValue('fields', data.document.fields);
                    form.setFieldValue('documentId', data.document.documentId);
                    form.setFieldValue('name', data.document.name);
                } else {
                    console.error('Could not find document.')
                }
            },
            onFailure: (err) => {
                console.error(err);
            },
            message: { success: 'Successfully got document. Save to continue.', error: 'Something went wrong fetching the default document!' },
            headers: {
                Authorization: `Bearer ${accounts[0].idToken}`,
            },
        }))
    }, []);

    return (
        <Form
            form={form}
            initialValues={document}
            onFinish={onFinish}
        >
            <Form.Item style={{ display: 'none' }} name='id' />
            <Row gutter={8}>
                <Col span={12}>
                    <Form.Item
                        name='name'
                        label='Name'
                    >
                        <Input placeholder="Name" />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={8} justify='space-between'>
                <Col span={18}>
                    <Form.Item
                        name='documentId'
                        label='Document ID'
                    >
                        <Input placeholder="Name" />
                    </Form.Item>
                </Col>
                <Col
                    span={5}
                >
                    <Form.Item
                        name='required'
                        label='Required'
                    >
                        <Switch />
                    </Form.Item>
                </Col>
            </Row>
            <Divider className="mt-1" />
            <Row align='middle' justify='space-between' className="mb-2">
                <Typography.Title level={5} className="mt-0 mb-0-i">Fields</Typography.Title>
                <Col>
                    <Upload
                        accept='application/json'
                        maxCount={1}
                        multiple={false}
                        onChange={onChange}
                        beforeUpload={() => true}
                        showUploadList={false}
                        customRequest={(_options) => { }}
                    >
                        <Button icon={<UploadOutlined />}>Import from SigSign</Button>
                    </Upload>
                    <Button
                        className="ml-2"
                        icon={<HeartOutlined />}
                        onClick={getDefault}
                    >
                        Default
                    </Button>
                </Col>
            </Row>
            <Form.List name='fields'>
                {(fields, { add, remove }) => (
                    <>
                        {fields.map((field) => (
                            <Row gutter={8} key={`${field.name}${field.key}`}>
                                <Col span={5}>
                                    <Form.Item name={[field.name, 'fieldName']}>
                                        <Input
                                            placeholder="Field Name"
                                            addonAfter={(
                                                <Tooltip title='This is not required, just for display here, not on document.' trigger={"click"}>
                                                    <InfoCircleOutlined />
                                                </Tooltip>
                                            )}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={5}>
                                    <Form.Item name={[field.name, 'fieldId']} rules={[{ required: true, message: 'Field ID is Required' }]}>
                                        <Input placeholder="Field ID" />
                                    </Form.Item>
                                </Col>
                                <Col span={5}>
                                    <Form.Item name={[field.name, 'defaultValue']}>
                                        <Input placeholder="Default Value" />
                                    </Form.Item>
                                </Col>
                                <Col span={5}>
                                    <Form.Item name={[field.name, 'populatedValue']}>
                                        <Select
                                            placeholder="Populated Value"
                                            options={[
                                                { label: 'First Name', value: 'firstName' },
                                                { label: 'Last Name', value: 'lastName' },
                                                { label: 'TIN', value: 'tin' },
                                                { label: 'Email', value: 'email' },
                                                { label: 'Address Line 1', value: 'addressLine1' },
                                                { label: 'Address Line 2', value: 'addressLine2' },
                                                { label: 'City', value: 'city' },
                                                { label: 'State', value: 'state' },
                                                { label: 'Postal Code', value: 'postalCode' },
                                                { label: 'Name', value: 'name' },
                                                { label: 'Previous Years', value: 'previousYears' },
                                                { label: 'Previous Years from Order', value: 'previousYearsOnOrder' },
                                                { label: 'Future Years', value: 'futureYears' },
                                                { label: 'Form Number', value: 'formNumber' },
                                                { label: 'Business Name', value: 'businessName' },
                                                { label: 'Business EIN', value: 'ein' },
                                                { label: 'Business Email', value: 'businessEmail' },
                                                { label: 'Business Title', value: 'title' },
                                                { label: 'Business Address Line 1', value: 'businessAddressLine1' },
                                                { label: 'Business Address Line 2', value: 'businessAddressLine2' },
                                                { label: 'Business City', value: 'businessCity' },
                                                { label: 'Business State', value: 'businessState' },
                                                { label: 'Business Postal Code', value: 'businessPostalCode' },
                                                { label: 'Business Form Number', value: 'businessFormNumber' },
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={4} style={{ textAlign: 'right' }}>
                                    <Button
                                        danger
                                        onClick={() => { remove(field.name) }}
                                    >
                                        Remove
                                    </Button>
                                </Col>
                            </Row>
                        ))}
                        <Button type="dashed" onClick={() => add()} block>
                            + Add Field
                        </Button>
                    </>
                )}
            </Form.List>
            <Row
                align='bottom'
                justify='end'
                gutter={8}
                className="mt-2"
            >
                {onCancel && <Col onClick={onCancel}><Button>Cancel</Button></Col>}
                <Col>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                    >
                        Save
                    </Button>
                </Col>
            </Row>
        </Form >
    );
}

const SignatureDocumentModal = ({
    id,
    onClose,
    updateDocument,
    document,
    enabled,
}: SignatureDocumentModalParams) => {
    return (
        <Modal
            onCancel={onClose}
            open={Boolean(id)}
            title="Document"
            destroyOnHidden
            footer={null}
            width='50%'
        >
            <SignatureDocumentModalForm
                onCancel={onClose}
                document={document}
                onFinish={(values) => { updateDocument(values, -1); }}
                enabled={enabled}
            />
        </Modal>
    );
};

export default SignatureDocumentModal;
